/**
 * The free-trial offer for THIS buyer — the pitch a Plus gate may make.
 *
 * Product rule: the trial NEVER starts on its own. Signup doesn't touch it;
 * the user discovers free opncue first, and only when they hit a Plus
 * feature or limit do we say "Try Plus free for N days". Starting it is
 * always their explicit checkout/store-sheet confirmation.
 *
 * WHO gets the pitch is the backend's policy (the `trial:` block in
 * server.yaml — mode / per-user grant / account age / triggers), asked via
 * the platform provider. `trigger` names the surface asking, so the policy
 * can allow some moments and not others — which is why the session cache is
 * per trigger. A Plus user never has an offer.
 */
import { useEffect, useState } from "react";

import { getPaymentProvider } from "@/lib/payments";
import { useAuthStore } from "@/state/auth-store";

/** Surfaces that pitch the trial, named for the policy's allowlist. */
export type TrialTrigger =
  | "free_limit_reached"
  | "plus_feature_attempted"
  | "plus_paywall_opened";

export type TrialOffer = {
  days: number;
  /** The first-charge date ("21 Aug 2026"), fixed when the offer arrived. */
  firstCharge: string;
};

const cached = new Map<TrialTrigger, TrialOffer | null>();
const inflight = new Map<TrialTrigger, Promise<TrialOffer | null>>();

export async function fetchTrialOffer(
  trigger: TrialTrigger,
): Promise<TrialOffer | null> {
  if (cached.has(trigger)) return cached.get(trigger) ?? null;
  let pending = inflight.get(trigger);
  if (!pending) {
    pending = getPaymentProvider()
      .trialInfo(trigger)
      .then((offer) => {
        const resolved = offer
          ? {
              days: offer.days,
              firstCharge: new Date(
                Date.now() + offer.days * 24 * 3600 * 1000,
              ).toLocaleDateString(undefined, {
                day: "numeric",
                month: "short",
                year: "numeric",
              }),
            }
          : null;
        cached.set(trigger, resolved);
        return resolved;
      })
      .catch(() => {
        // No answer = no pitch. Not cached — the next gate asks again.
        inflight.delete(trigger);
        return null;
      });
    inflight.set(trigger, pending);
  }
  return pending;
}

/** The current offer for a surface, or null. Plus users always get null. */
export function useTrialOffer(
  trigger: TrialTrigger,
  enabled = true,
): TrialOffer | null {
  const plan = useAuthStore((state) => state.user?.plan);
  const [offer, setOffer] = useState<TrialOffer | null>(
    cached.get(trigger) ?? null,
  );

  useEffect(() => {
    if (!enabled || plan === "plus" || cached.has(trigger)) return;
    let live = true;
    void fetchTrialOffer(trigger).then((resolved) => {
      if (live) setOffer(resolved);
    });
    return () => {
      live = false;
    };
  }, [enabled, plan, trigger]);

  return enabled && plan !== "plus" ? offer : null;
}
